<template>
  <div :class="t.wrapperLoose">
    <label :class="t.label">
      {{ question.prompt }}
      <span v-if="!question.required" class="text-gray-400 font-normal">(optional)</span>
    </label>
    <p
      v-if="question.subtext"
      :class="t.subtext"
      v-html="question.subtext"
    ></p>

    <!-- Yes/No Buttons -->
    <div class="flex gap-4">
      <button
        v-for="option in question.answers"
        :key="option.prompt"
        @click="selectOption(option)"
        type="button"
        :class="t.splitButton"
        :style="buttonStyle(option)"
        @mouseenter="hoveredOption = option.prompt"
        @mouseleave="hoveredOption = null"
      >
        {{ option.prompt }}
      </button>
    </div>

    <!-- Additional Info Input (if needed) -->
    <div v-if="needsAdditionalInfo" class="mt-4 space-y-2">
      <label :class="t.subLabel">
        {{ additionalInfoPrompt }}
      </label>
      <input
        v-model="additionalInfo"
        type="text"
        :class="[t.input, additionalInfoError ? 'border-red-500' : 'border-gray-300']"
        :style="inputFocusStyle"
        @focus="inputFocused = true"
        @blur="inputFocused = false; validate()"
        placeholder="Please provide details"
      />
    </div>

    <!-- Error Message -->
    <p v-if="error" :class="t.error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { tokensFor } from './size_tokens';

export default {
  name: 'QuestionYesNo',
  props: {
    question: {
      type: Object,
      required: true,
    },
    existingAnswer: {
      type: Object,
      default: null,
    },
    size: {
      type: String,
      default: 'kiosk',
      validator: (v) => ['kiosk', 'form'].includes(v),
    },
  },
  data() {
    return {
      answer: {
        answer: '',
        addtl_info: '',
      },
      additionalInfo: '',
      originalAnswer: {
        answer: '',
        addtl_info: '',
      },
      error: false,
      additionalInfoError: false,
      errorMessage: 'Please select an answer.',
      hoveredOption: null,
      inputFocused: false,
    };
  },
  computed: {
    t() {
      return tokensFor(this.size);
    },
    inputFocusStyle() {
      if (this.inputFocused) {
        return {
          borderColor: 'var(--team-primary)',
          boxShadow: '0 0 0 3px rgba(var(--team-primary-rgb, 55, 65, 81), 0.1)',
        };
      }
      return {};
    },
    selectedOption() {
      if (!this.answer.answer) return null;
      return this.question.answers.find((a) => a.prompt === this.answer.answer);
    },
    needsAdditionalInfo() {
      return this.selectedOption && this.selectedOption.addtl_info;
    },
    additionalInfoPrompt() {
      return this.selectedOption ? this.selectedOption.addtl_prompt : '';
    },
    dirty() {
      return this.answer.answer !== this.originalAnswer.answer
        || this.answer.addtl_info !== this.originalAnswer.addtl_info;
    },
    valid() {
      if (this.question.required) {
        if (!this.answer.answer || this.answer.answer.length === 0) {
          return false;
        }
      }
      // If we need additional info, it must be provided
      if (this.needsAdditionalInfo) {
        return this.additionalInfo.length > 0;
      }
      // If not required and empty, valid
      if (!this.question.required && (!this.answer.answer || this.answer.answer.length === 0)) {
        return true;
      }
      return this.answer.answer.length > 0;
    },
  },
  watch: {
    'answer.answer': function () {
      this.updateAnswerObject();
      this.emit();
      if (this.valid) {
        this.error = false;
      }
    },
    additionalInfo() {
      this.updateAnswerObject();
      this.emit();
      if (this.valid) {
        this.additionalInfoError = false;
        this.error = false;
      }
    },
  },
  created() {
    if (this.existingAnswer && this.existingAnswer.answer) {
      // Answer is stored as object: { answer: "Yes", addtl_info: "..." }
      const existing = this.existingAnswer.answer;
      if (typeof existing === 'object') {
        this.answer = { ...existing };
        this.additionalInfo = existing.addtl_info || '';
        this.originalAnswer = {
          answer: existing.answer || '',
          addtl_info: existing.addtl_info || '',
        };
      } else {
        // Legacy format - just a string
        this.answer.answer = existing;
        this.originalAnswer = { answer: existing, addtl_info: '' };
      }
    }
    this.$notificationManager?.on('show-reg-errors', this.validate);
  },
  destroyed() {
    this.$notificationManager?.off('show-reg-errors', this.validate);
  },
  methods: {
    buttonStyle(option) {
      const isSelected = this.answer.answer === option.prompt;
      const isHovered = this.hoveredOption === option.prompt;

      if (isSelected) {
        return {
          backgroundColor: 'var(--team-primary)',
          borderColor: 'var(--team-primary)',
          color: 'var(--team-text)',
        };
      }
      if (isHovered) {
        return {
          borderColor: 'var(--team-primary)',
          backgroundColor: 'transparent',
          color: '#374151',
        };
      }
      return {
        borderColor: '#D1D5DB',
        backgroundColor: 'transparent',
        color: '#374151',
      };
    },
    selectOption(option) {
      this.answer.answer = option.prompt;
      // Reset additional info if switching options
      if (!option.addtl_info) {
        this.additionalInfo = '';
      }
    },
    updateAnswerObject() {
      this.answer.addtl_info = this.needsAdditionalInfo ? this.additionalInfo : '';
    },
    emit() {
      this.$emit('answer-changed', this.question, this.answer, this.dirty, this.valid);
    },
    validate() {
      if (!this.valid) {
        this.error = true;
        if (!this.answer.answer || this.answer.answer.length === 0) {
          this.errorMessage = 'Please select an answer.';
        } else if (this.needsAdditionalInfo && this.additionalInfo.length === 0) {
          this.errorMessage = 'Please provide additional details.';
          this.additionalInfoError = true;
        }
      } else {
        this.error = false;
        this.additionalInfoError = false;
      }
    },
  },
};
</script>
